import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download, ExternalLink, FileSpreadsheet, FileText, Image as ImageIcon, Loader2, Paperclip } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { cn } from '@/lib/utils'
import { getApiErrorMessage } from '@/lib/api-error'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'

/** A file behind a piece of evidence: an invoice or PO PDF, a quote, an email's attachment. */
export type EvidenceDocument = {
  key: string
  label: string
  filename: string
  contentType: string
  /** Dashboard API path that serves the bytes. */
  path: string
  /** Dashboard API path that returns a spreadsheet as tables, for .xlsx/.xls/.csv files. */
  sheetPath?: string
}

type SheetPreview = {
  filename: string
  sheets: { name: string; columns: string[]; rows: string[][]; row_count: number }[]
}

type Kind = 'pdf' | 'image' | 'sheet' | 'other'

const IMAGE_TYPES = /^image\/(png|jpeg|gif|webp|avif|bmp)$/

function documentKind(document: Pick<EvidenceDocument, 'contentType' | 'filename' | 'sheetPath'>): Kind {
  const name = document.filename.toLowerCase()
  if (document.contentType === 'application/pdf' || name.endsWith('.pdf')) return 'pdf'
  if (IMAGE_TYPES.test(document.contentType) || /\.(png|jpe?g|gif|webp|avif|bmp)$/.test(name)) return 'image'
  if (document.sheetPath && /\.(xlsx|xlsm|xls|csv)$/.test(name)) return 'sheet'
  return 'other'
}

const ICONS = { pdf: FileText, image: ImageIcon, sheet: FileSpreadsheet, other: Paperclip }

async function fetchBlob(path: string, token: string | null) {
  if (!token) throw new Error('Not signed in')
  const response = await fetch(`${API_BASE_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } })
  if (!response.ok) throw new Error(response.status === 404 ? 'The file is no longer available' : `Failed (${response.status})`)
  return response.blob()
}

/** The file as an object URL while it is shown; revoked when it closes or changes. */
function useObjectUrl(path: string) {
  const { getAccessToken } = useAuth()
  const { data: blob, error } = useQuery({
    queryKey: ['evidence-file', path],
    queryFn: async () => fetchBlob(path, await getAccessToken()),
    staleTime: 5 * 60_000,
  })
  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob])
  useEffect(() => () => {
    if (url) URL.revokeObjectURL(url)
  }, [url])
  return { url, error: error ? (getApiErrorMessage(error, 'Could not load the file')) : null }
}

function Loading() {
  return (
    <div className="flex h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="size-4 animate-spin" aria-hidden /> Loading…
    </div>
  )
}

function Failed({ message }: { message: string }) {
  return <p className="py-6 text-center text-sm text-muted-foreground">{message}</p>
}

function FileView({ document, kind }: { document: EvidenceDocument; kind: 'pdf' | 'image' }) {
  const { url, error } = useObjectUrl(document.path)
  if (error) return <Failed message={error} />
  if (!url) return <Loading />
  return kind === 'pdf' ? (
    <iframe title={document.filename} src={url} className="h-[70vh] max-h-[760px] w-full rounded-md border bg-muted" />
  ) : (
    <img src={url} alt={document.filename} className="mx-auto max-h-[70vh] rounded-md border object-contain" />
  )
}

function SheetView({ document }: { document: EvidenceDocument }) {
  const { getAccessToken } = useAuth()
  const [tab, setTab] = useState(0)
  const { data, error, isLoading } = useQuery({
    queryKey: ['evidence-sheet', document.sheetPath],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}${document.sheetPath}`, {
        headers: { Authorization: `Bearer ${(await getAccessToken()) ?? ''}` },
      })
      if (!response.ok) throw new Error("This file can't be shown as a table; download it instead")
      return (await response.json()) as SheetPreview
    },
    staleTime: 5 * 60_000,
  })
  if (isLoading) return <Loading />
  if (error || !data) return <Failed message={getApiErrorMessage(error, 'Could not read the sheet')} />
  const sheet = data.sheets[tab] ?? data.sheets[0]
  if (!sheet) return <Failed message="The sheet is empty" />
  return (
    <div className="space-y-2">
      {data.sheets.length > 1 && (
        <div role="tablist" aria-label="Sheets" className="flex flex-wrap gap-1">
          {data.sheets.map((item, index) => (
            <button
              key={item.name}
              type="button"
              role="tab"
              aria-selected={index === tab}
              onClick={() => setTab(index)}
              className={cn(
                'cursor-pointer rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted',
                index === tab && 'bg-muted text-foreground',
              )}
            >
              {item.name}
            </button>
          ))}
        </div>
      )}
      <div className="max-h-[60vh] overflow-auto rounded-md border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-muted">
            <tr>
              {sheet.columns.map((column, index) => (
                <th key={index} className="whitespace-nowrap px-2 py-1.5 text-left font-medium">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {sheet.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {sheet.columns.map((_, index) => (
                  <td key={index} className="whitespace-nowrap px-2 py-1 tabular-nums">
                    {row[index] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sheet.row_count > sheet.rows.length && (
        <p className="text-xs text-muted-foreground">
          First {sheet.rows.length} of {sheet.row_count.toLocaleString()} rows; download for the rest.
        </p>
      )}
    </div>
  )
}

function useFileActions(document: EvidenceDocument) {
  const { getAccessToken } = useAuth()
  const run = async (open: boolean) => {
    // A new tab opens during the click, before the awaits, so pop-up blockers let it through.
    const win = open ? window.open('about:blank', '_blank') : null
    try {
      const blob = await fetchBlob(document.path, await getAccessToken())
      const url = URL.createObjectURL(blob)
      if (win) {
        win.opener = null
        win.location.replace(url)
      } else {
        const link = window.document.createElement('a')
        link.href = url
        link.download = document.filename
        link.click()
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (error) {
      win?.close()
      toast.error(`Could not open ${document.filename}: ${getApiErrorMessage(error)}`)
    }
  }
  return { download: () => void run(false), openTab: () => void run(true) }
}

function Viewer({ document }: { document: EvidenceDocument }) {
  const kind = documentKind(document)
  const { download, openTab } = useFileActions(document)
  return (
    <div className="space-y-2 pt-1">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <p className="min-w-0 flex-1 truncate text-xs font-medium" title={document.filename}>
          {document.filename}
        </p>
        {(kind === 'pdf' || kind === 'image') && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={openTab}>
            <ExternalLink className="size-3.5" aria-hidden /> New tab
          </Button>
        )}
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={download}>
          <Download className="size-3.5" aria-hidden /> Download
        </Button>
      </div>
      {kind === 'pdf' || kind === 'image' ? (
        <FileView document={document} kind={kind} />
      ) : kind === 'sheet' ? (
        <SheetView document={document} />
      ) : (
        <Failed message="This file opens after you download it." />
      )}
    </div>
  )
}

/**
 * The files behind a piece of evidence as a row of chips; one click shows the file in place: a PDF in a viewer,
 * an image, a spreadsheet as a table (USA-1513). Click it again to close.
 */
export function EvidenceDocuments({ documents, className }: { documents: EvidenceDocument[]; className?: string }) {
  const [openKey, setOpenKey] = useState<string | null>(null)
  if (documents.length === 0) return null
  const open = documents.find((document) => document.key === openKey)
  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap gap-1.5">
        {documents.map((document) => {
          const Icon = ICONS[documentKind(document)]
          const shown = document.key === openKey
          return (
            <button
              key={document.key}
              type="button"
              aria-pressed={shown}
              title={document.filename}
              onClick={() => setOpenKey(shown ? null : document.key)}
              className={cn(
                'inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-full border bg-background px-2.5 py-1 text-xs font-medium text-foreground/80 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                shown && 'border-foreground/30 bg-muted text-foreground',
              )}
            >
              <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <span className="truncate">{document.label}</span>
            </button>
          )
        })}
      </div>
      {open && <Viewer key={open.key} document={open} />}
    </div>
  )
}
